import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useIntl } from 'react-intl';

import { Button, H2, IconCollection, SkeletonCard } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { getCountryName } from '../../../util/data';
import { createResourceLocatorString } from '../../../util/routes';
import { getAlgoliaIndexNameForLang } from '../../../util/urlHelpers';
import { SUPPORTED_LANGUAGES } from '../../../util/constants';
import { queryAlgolia } from '../../../util/api';
import { setCUserCurrencyAndLocation } from '../../../ducks/user.duck';
import { fetchExchangeRates } from '../../../ducks/exchangeRates.duck';
import { useLocationBasedPhoneNumber } from '../../../hooks/useLocationBasedPhoneNumber';
import ListCard from '../../SearchPage/AlgoliaSearchPage/MainSearchContent/Card/ListCard';

import css from './TrustedLocalRentalsSection.module.css';

const MAX_LISTINGS = 8;
const DEFAULT_MINIMUM_HITS = MAX_LISTINGS;
const IS_DESKTOP = typeof window !== 'undefined' && window.innerWidth > 1024;

const resolveLanguageIndex = () => {
    if (typeof window === 'undefined') {
        return getAlgoliaIndexNameForLang('en');
    }

    const locationPath = window.location.pathname || '';
    const storedLanguage = typeof localStorage !== 'undefined' ? localStorage.getItem('appLanguage') : null;

    const activeLanguage =
        storedLanguage ||
        SUPPORTED_LANGUAGES.find(
            code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
        ) ||
        'en';

    return getAlgoliaIndexNameForLang(activeLanguage);
};

const TrustedLocalRentalsSection = props => {
    const { sectionId = 'trusted-local-rentals' } = props;

    const dispatch = useDispatch();
    const intl = useIntl();
    const history = useHistory();
    const routeConfiguration = useRouteConfiguration();
    const config = useConfiguration();

    const { currentUser, currentUserCurrency, currentUserLocationInfo } = useSelector(state => state.user);
    const { exchangeRates = [], fetchRatesInProgress } = useSelector(state => state.exchangeRate || {});

    const sectionRef = useRef(null);
    const idleCallbackRef = useRef(null);
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const [shouldFetch, setShouldFetch] = useState(false);
    const [listings, setListings] = useState([]);
    const [fallbackMeta, setFallbackMeta] = useState({
        usedFallback: false,
        source: null,
        hadLocalMatches: false,
    });
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [headingKey, setHeadingKey] = useState('TrustedLocalRentalsSection.title');
    const [swiperComponents, setSwiperComponents] = useState(null);
    const [swiperModules, setSwiperModules] = useState(null);

    const locationBasedPhoneNumber = useLocationBasedPhoneNumber(process.env.REACT_APP_TEAM_PHONE_NUMBER);

    // Ensure we have currency/location for the current visitor
    useEffect(() => {
        if (!currentUserLocationInfo?.origin) {
            dispatch(setCUserCurrencyAndLocation(currentUser)).catch(() => {
                // Silent failure - we fall back to default location
            });
        }
    }, [dispatch, currentUser, currentUserLocationInfo?.origin]);

    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        if ((!exchangeRates || exchangeRates.length === 0) && !fetchRatesInProgress) {
            dispatch(fetchExchangeRates());
        }
    }, [dispatch, exchangeRates, shouldFetch, fetchRatesInProgress]);

    const locationDetails = useMemo(() => {
        const defaultLocation = config?.defaultLocation || {};
        const userOrigin = currentUserLocationInfo?.origin;
        const userCountryCode = currentUserLocationInfo?.country;

        if (userOrigin?.lat && userOrigin?.lng && userCountryCode) {
            return {
                source: 'user',
                origin: { lat: userOrigin.lat, lng: userOrigin.lng },
                countryCode: userCountryCode,
                countryName: getCountryName(userCountryCode),
                address: currentUserLocationInfo?.address,
                city: currentUserLocationInfo?.city,
                bounds: currentUserLocationInfo?.bounds,
            };
        }

        if (defaultLocation?.latLng && defaultLocation?.country) {
            return {
                source: 'default',
                origin: { lat: defaultLocation.latLng[0], lng: defaultLocation.latLng[1] },
                countryCode: defaultLocation.countryCode,
                countryName: defaultLocation.country,
                address: defaultLocation.address,
                city: defaultLocation.city,
                bounds: defaultLocation.bounds,
            };
        }

        return null;
    }, [config, currentUserLocationInfo]);

    const hasUserLocation = locationDetails?.source === 'user';

    useEffect(() => {
        if (shouldFetch || typeof window === 'undefined') {
            return undefined;
        }

        const node = sectionRef.current;
        if (!node) {
            return undefined;
        }

        const requestIdle =
            window.requestIdleCallback ||
            function cbFallback(callback) {
                return window.setTimeout(callback, 1500);
            };
        const cancelIdle = window.cancelIdleCallback || window.clearTimeout;

        const triggerFetch = () => setShouldFetch(true);

        // Not available in Safari < 12.1 (e.g. Safari 11.1/High Sierra). Skip it there and rely on
        // the idleCallback fallback below, which already triggers the fetch unconditionally.
        const observer = typeof IntersectionObserver !== 'undefined'
            ? new IntersectionObserver(
                entries => {
                    if (entries.some(entry => entry.isIntersecting)) {
                        triggerFetch();
                        observer.disconnect();
                        if (idleCallbackRef.current) {
                            cancelIdle(idleCallbackRef.current);
                            idleCallbackRef.current = null;
                        }
                    }
                },
                { rootMargin: '200px 0px' }
            )
            : null;

        observer?.observe(node);

        idleCallbackRef.current = requestIdle(() => {
            triggerFetch();
            observer?.disconnect();
            idleCallbackRef.current = null;
        });

        return () => {
            observer?.disconnect();
            if (idleCallbackRef.current) {
                cancelIdle(idleCallbackRef.current);
                idleCallbackRef.current = null;
            }
        };
    }, [shouldFetch]);

    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        if (!locationDetails?.origin?.lat || !locationDetails?.origin?.lng) {
            return;
        }

        let isMounted = true;
        const fetchListings = async () => {
            setStatus('loading');
            setError(null);

            try {
                const indexName = resolveLanguageIndex();
                const aroundLatLng = `${locationDetails.origin.lat}, ${locationDetails.origin.lng}`;
                const sortBy = hasUserLocation ? 'nearest' : 'createdAt_desc';

                const response = await queryAlgolia({
                    query: '',
                    filters: '',
                    sort: sortBy,
                    isNearestSort: sortBy === 'nearest',
                    aroundLatLng,
                    country: locationDetails.countryName,
                    minimumHits: DEFAULT_MINIMUM_HITS,
                    indexes: {
                        listingIndex: { indexName },
                    },
                });

                const {
                    listings: relevantListings = [],
                    farawayRelevantListings = [],
                    irrelevantListings = [],
                } = response?.data || {};

                const limitedIrrelevant = irrelevantListings.slice(0, MAX_LISTINGS);
                const limitedFaraway = farawayRelevantListings.slice(0, MAX_LISTINGS);
                const limitedRelevant = relevantListings.slice(0, MAX_LISTINGS);

                const hasLocalResults = limitedRelevant.length > 0;

                let combinedListings = [...limitedRelevant];
                let usedFallback = false;
                let fallbackSource = null;

                if (combinedListings.length < MAX_LISTINGS) {
                    const remainingAfterRelevant = MAX_LISTINGS - combinedListings.length;
                    const farawayToUse = limitedFaraway.slice(0, remainingAfterRelevant);
                    combinedListings = [...combinedListings, ...farawayToUse];

                    if (farawayToUse.length > 0) {
                        usedFallback = true;
                        fallbackSource = 'faraway';
                    }

                    const remainingAfterFaraway = MAX_LISTINGS - combinedListings.length;
                    if (remainingAfterFaraway > 0) {
                        const irrelevantToUse = limitedIrrelevant.slice(0, remainingAfterFaraway);
                        if (irrelevantToUse.length > 0) {
                            combinedListings = [...combinedListings, ...irrelevantToUse];
                            usedFallback = true;
                            fallbackSource = fallbackSource || 'irrelevant';
                        }
                    }
                }

                if (combinedListings.length === 0 && limitedIrrelevant.length > 0) {
                    combinedListings = limitedIrrelevant.slice(0, MAX_LISTINGS);
                    usedFallback = true;
                    fallbackSource = 'irrelevant';
                }

                if (isMounted) {
                    setListings(combinedListings);
                    setFallbackMeta({
                        usedFallback,
                        source: fallbackSource,
                        hadLocalMatches: hasLocalResults,
                    });
                    setHeadingKey(() => {
                        if (!hasUserLocation) {
                            return 'TrustedLocalRentalsSection.fallbackTitle';
                        }

                        if (hasLocalResults) {
                            return 'TrustedLocalRentalsSection.title';
                        }

                        if (combinedListings.length > 0) {
                            return 'TrustedLocalRentalsSection.fallbackTitle';
                        }

                        return 'TrustedLocalRentalsSection.title';
                    });
                    setStatus('loaded');
                }
            } catch (fetchError) {
                if (isMounted) {
                    setStatus('error');
                    setError(fetchError);
                }
            }
        };

        fetchListings();

        return () => {
            isMounted = false;
        };
    }, [locationDetails, shouldFetch, hasUserLocation]);

    useEffect(() => {
        if (status === 'idle') {
            setHeadingKey(hasUserLocation ? 'TrustedLocalRentalsSection.title' : 'TrustedLocalRentalsSection.fallbackTitle');
        }
    }, [hasUserLocation, status]);

    // Dynamically load Swiper components when we have listings or when shouldFetch is true
    useEffect(() => {
        if (!shouldFetch && listings.length === 0) {
            return;
        }

        let isMounted = true;

        const importWithRetry = fn =>
            fn().catch(() => new Promise(res => setTimeout(() => res(fn()), 1500)));

        const loadSwiper = async () => {
            try {
                const [{ Swiper, SwiperSlide }, { Navigation, Pagination }] = await Promise.all([
                    importWithRetry(() => import(/* webpackChunkName: "swiper-react" */ 'swiper/react')),
                    importWithRetry(() => import(/* webpackChunkName: "swiper-modules" */ 'swiper/modules')),
                ]);

                if (isMounted) {
                    setSwiperComponents({ Swiper, SwiperSlide });
                    setSwiperModules({ Navigation, Pagination });
                }
            } catch (error) {
                console.error('Failed to load Swiper:', error);
            }
        };

        loadSwiper();

        return () => {
            isMounted = false;
        };
    }, [shouldFetch, listings.length]);

    const hasListings = listings.length > 0;
    const showSkeletons = shouldFetch && status === 'loading';
    const swiperReady = swiperComponents && swiperModules;

    const handleViewMore = () => {
        if (!locationDetails) {
            history.push(
                createResourceLocatorString('SearchPage', routeConfiguration, {}, { sort: 'createdAt_desc' }),
                { fromLandingPage: true }
            );
            return;
        }

        const searchParams = hasUserLocation
            ? {
                ...(locationDetails.address ? { address: locationDetails.address } : {}),
                ...(locationDetails.bounds ? { bounds: locationDetails.bounds } : {}),
                ...(locationDetails.city ? { city: locationDetails.city } : {}),
                ...(locationDetails.countryName ? { country: locationDetails.countryName } : {}),
                ...(locationDetails.origin
                    ? {
                        origin: {
                            lat: locationDetails.origin.lat,
                            lng: locationDetails.origin.lng,
                        },
                    }
                    : {}),
            }
            : { sort: 'createdAt_desc' };

        history.push(
            createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams),
            { fromLandingPage: true }
        );
    };

    const sectionClasses = classNames(css.root, {
        [css.loading]: showSkeletons,
    });

    return (
        <section
            id={sectionId}
            ref={sectionRef}
            className={sectionClasses}
            aria-label="Trusted local rentals"
        >
            <div className={css.container}>
                <div className={css.header}>
                    <H2 className={css.title}>
                        {intl.formatMessage({ id: headingKey })}
                    </H2>
                    <p className={css.subtitle}>
                        {intl.formatMessage({ id: IS_DESKTOP ? 'TrustedLocalRentalsSection.subtitleDesktop' : 'TrustedLocalRentalsSection.subtitleMobile' })}
                    </p>
                </div>

                <div className={css.carouselWrapper}>
                    {showSkeletons ? (
                        <div className={css.skeletonRow}>
                            {Array.from({ length: Math.min(MAX_LISTINGS, 4) }).map((_, index) => (
                                <SkeletonCard
                                    key={`trusted-skeleton-${index}`}
                                    className={css.skeletonCard}
                                />
                            ))}
                        </div>
                    ) : hasListings ? (
                        swiperReady ? (
                            <>
                                <button
                                    ref={navigationPrevRef}
                                    className={css.navigationPrev}
                                    aria-label="Previous slide"
                                    type="button"
                                >

                                </button>
                                <button
                                    ref={navigationNextRef}
                                    className={css.navigationNext}
                                    aria-label="Next slide"
                                    type="button"
                                >

                                </button>
                                <swiperComponents.Swiper
                                    modules={[swiperModules.Navigation, swiperModules.Pagination]}
                                    navigation={{
                                        prevEl: navigationPrevRef.current,
                                        nextEl: navigationNextRef.current,
                                    }}
                                    pagination={{
                                        clickable: true,
                                        dynamicBullets: false,
                                    }}
                                    onBeforeInit={(swiper) => {
                                        swiper.params.navigation.prevEl = navigationPrevRef.current;
                                        swiper.params.navigation.nextEl = navigationNextRef.current;
                                    }}
                                    spaceBetween={16}
                                    slidesPerView="auto"
                                    slidesPerGroup={5}
                                    speed={600}
                                    breakpoints={{
                                        320: {
                                            slidesPerGroup: 1,
                                            spaceBetween: 16,
                                        },
                                        640: {
                                            slidesPerGroup: 2,
                                            spaceBetween: 16,
                                        },
                                        1024: {
                                            slidesPerGroup: 3,
                                            spaceBetween: 16,
                                        },
                                        1280: {
                                            slidesPerGroup: 4,
                                            spaceBetween: 16,
                                        },
                                        1536: {
                                            slidesPerGroup: 4,
                                            spaceBetween: 16,
                                        },
                                    }}
                                    className={css.swiper}
                                >
                                    {listings.map((listing, index) => (
                                        <swiperComponents.SwiperSlide key={listing.objectID} className={css.swiperSlide}>
                                            <ListCard
                                                rental={listing}
                                                layout="carousel"
                                                currentUserCurrency={currentUserCurrency}
                                                exchangeRates={exchangeRates}
                                                history={history}
                                                routes={routeConfiguration}
                                                currentUserCountryCode={locationDetails?.countryCode || config?.defaultLocation?.countryCode}
                                                locale={intl.locale}
                                                hasLocationAvailable={hasUserLocation && fallbackMeta.hadLocalMatches}
                                                isCritical={index === 0}
                                                locationBasedPhoneNumber={locationBasedPhoneNumber}
                                            />
                                        </swiperComponents.SwiperSlide>
                                    ))}
                                </swiperComponents.Swiper>
                            </>
                        ) : (
                            <div className={css.skeletonRow}>
                                {Array.from({ length: Math.min(MAX_LISTINGS, 4) }).map((_, index) => (
                                    <SkeletonCard
                                        key={`swiper-loading-skeleton-${index}`}
                                        className={css.skeletonCard}
                                    />
                                ))}
                            </div>
                        )
                    ) : null}
                </div>

                <div className={css.footer}>
                    <Button
                        type="button"
                        onClick={handleViewMore}
                        className={css.ctaButton}
                        disabled={status === 'loading' && !hasListings}
                        variant={"secondary-outline"}
                        arrow={"right"}
                        icon={<IconCollection name="arrow_righ_stright" />}
                    >
                        <span>{intl.formatMessage({ id: 'TrustedLocalRentalsSection.viewMore' })}</span>

                    </Button>
                </div>

                {status === 'error' ? (
                    <div className={css.errorBanner} role="alert">
                        {intl.formatMessage({ id: 'TrustedLocalRentalsSection.error' })}
                        {error?.message ? <span className={css.errorDetail}> — {error.message}</span> : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default TrustedLocalRentalsSection;

